<template>
  <div class="authorized-account-container">
    <div class="matrix-task-page">
      <!-- 顶部控制区：搜索框 & 分页 -->
      <div class="top-controls">
        <el-input v-model="searchQuery" placeholder="搜索任务标题或授权账号" clearable class="search-input"
          @keyup.enter="handleSearch"></el-input>
        <el-pagination class="pagination" background layout="prev, pager, next" :total="total" :page-size="pageSize"
          :current-page="currentPage" @current-change="handlePageChange"></el-pagination>
      </div>

      <!-- 按钮区：两个添加任务的按钮 -->
      <div class="button-area">
        <el-button class="task-button video-task-button" type="primary" @click="openAddVideoModal">
          添加视频任务
        </el-button>
        <el-button class="task-button image-text-task-button" type="primary" @click="openAddImageTextModal">
          添加图文任务
        </el-button>
      </div>

      <!-- 表格列表区域，使用骨架屏包裹 -->
      <el-skeleton :loading="loadingData" animated>
        <template #template>
          <div class="table-skeleton">
            <div v-for="n in 8" :key="n" class="skeleton-row">
              <el-skeleton-item variant="text" style="width: 30px; height: 20px;"></el-skeleton-item>
              <el-skeleton-item variant="text" style="width: 90px; height: 20px;"></el-skeleton-item>
              <el-skeleton-item variant="text" style="width: 90px; height: 20px;"></el-skeleton-item>
              <el-skeleton-item variant="text" style="width: 90px; height: 20px;"></el-skeleton-item>
              <el-skeleton-item variant="text" style="width: 90px; height: 20px;"></el-skeleton-item>
              <el-skeleton-item variant="text" style="width: 90px; height: 20px;"></el-skeleton-item>
              <el-skeleton-item variant="text" style="width: 90px; height: 20px;"></el-skeleton-item>
              <el-skeleton-item variant="text" style="width: 90px; height: 20px;"></el-skeleton-item>
              <el-skeleton-item variant="text" style="width: 90px; height: 20px;"></el-skeleton-item>
              <el-skeleton-item variant="text" style="width: 90px; height: 20px;"></el-skeleton-item>
              <el-skeleton-item variant="text" style="width: 90px; height: 20px;"></el-skeleton-item>
            </div>
          </div>
        </template>
        <template #default>
          <table class="accounts-table">
            <thead>
              <tr>
                <th>序号</th>
                <th>授权账号</th>
                <th>发布类型</th>
                <th class="ellipsis">素材地址</th>
                <th class="ellipsis">标题</th>
                <th class="ellipsis">简介</th>
                <th>发布状态</th>
                <th>更新时间</th>
                <th>创建时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(task, index) in paginatedTasks" :key="task.id">
                <td>{{ index + 1 + (currentPage - 1) * pageSize }}</td>
                <td>{{ task.zh_id }}</td>
                <td>{{ task.fb_type }}</td>
                <td class="ellipsis">{{ task.material_url }}</td>
                <td class="ellipsis">{{ task.fb_title }}</td>
                <td class="ellipsis">{{ task.fb_description }}</td>
                <td :class="getStatusClass(task.fb_status)">{{ task.fb_status }}</td>
                <td>{{ task.update_time }}</td>
                <td>{{ task.create_time }}</td>
                <td>
                  <el-button class="delete-button" size="small" @click="handleDelete(task)">删除</el-button>
                </td>
              </tr>
            </tbody>
          </table>
        </template>
      </el-skeleton>
    </div>

    <!-- 添加视频任务模态框 -->
    <el-dialog v-model="showAddVideoModal" title="添加视频任务" width="500px" :close-on-click-modal="false">
      <div class="add-form">
        <el-form :model="newVideoTask" label-width="80px">
          <el-form-item label="标题">
            <el-input v-model="newVideoTask.fb_title" placeholder="请输入标题"></el-input>
          </el-form-item>
          <el-form-item label="简介">
            <el-input v-model="newVideoTask.fb_description" type="textarea" placeholder="请输入简介"></el-input>
          </el-form-item>
          <el-form-item label="素材上传">
            <!-- 视频上传组件修改部分 -->
            <el-upload class="video-upload" :action="''" :http-request="uploadVideoRequest" :limit="1"
              :file-list="videoFileList" accept="video/*" :on-progress="handleVideoProgress">
              <div class="upload-area">
                <el-icon class="upload-icon">
                  <UploadFilled />
                </el-icon>
                <div class="el-upload__text">
                  <div v-if="videoProgress === 0">点击选择视频上传</div>
                  <div v-else-if="videoProgress < 100">上传中 {{ videoProgress }}%</div>
                  <div v-else>上传完成！</div>
                  <el-progress v-if="videoProgress > 0 && videoProgress < 100" :percentage="videoProgress"
                    :stroke-width="16" status="success" class="progress-bar" />
                </div>
              </div>
              <template #tip>
                <div class="el-upload__tip">
                  仅支持视频文件上传，最大200MB
                </div>
              </template>
            </el-upload>
          </el-form-item>
          <el-form-item label="授权账号">
            <el-select v-model="newVideoTask.zh_ids" multiple placeholder="请选择授权账号">
              <el-option v-for="item in authUserOptions" :key="item.id" :label="item.nickname"
                :value="item.id"></el-option>
            </el-select>
          </el-form-item>
        </el-form>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="closeAddVideoModal">取消</el-button>
          <el-button type="primary" :loading="videoAddLoading" @click="submitAddVideoTask">确定</el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 添加图文任务模态框 -->
    <el-dialog v-model="showAddImageTextModal" title="添加图文任务" width="500px" :close-on-click-modal="false">
      <div class="add-form">
        <el-form :model="newImageTextTask" label-width="80px">
          <el-form-item label="标题">
            <el-input v-model="newImageTextTask.fb_title" placeholder="请输入标题"></el-input>
          </el-form-item>
          <el-form-item label="简介">
            <el-input v-model="newImageTextTask.fb_description" type="textarea" placeholder="请输入简介"></el-input>
          </el-form-item>
          <el-form-item label="素材上传">
            <!-- 图片上传组件修改部分 -->
            <el-upload v-model:file-list="imageFileList" multiple :action="''" :http-request="uploadImageRequest"
              list-type="picture-card" accept="image/*" :limit="18" :on-progress="handleImageProgress">
              <el-icon>
                <Plus />
              </el-icon>
              <!-- 图片上传进度显示 -->
              <template #file="{ file }">
                <div class="image-upload-item">
                  <img class="el-upload-list__item-thumbnail" :src="file.url" alt="" />
                  <div v-if="file.progress < 100" class="progress-overlay">
                    <el-progress :percentage="file.progress" type="circle" :width="60" status="success" />
                  </div>
                </div>
              </template>
            </el-upload>

            <el-dialog v-model="dialogVisible" width="50%">
              <img :src="dialogImageUrl" alt="Preview Image" style="width: 100%;" />
            </el-dialog>
          </el-form-item>
          <el-form-item label="授权账号">
            <el-select v-model="newImageTextTask.zh_ids" multiple placeholder="请选择授权账号">
              <el-option v-for="item in authUserOptions" :key="item.id" :label="item.nickname"
                :value="item.id"></el-option>
            </el-select>
          </el-form-item>
        </el-form>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="closeAddImageTextModal">取消</el-button>
          <el-button type="primary" :loading="imageTextAddLoading" @click="submitAddImageTextTask">确定</el-button>
        </div>
      </template>
    </el-dialog>

  </div>
</template>

<script>
import {
  ElInput,
  ElButton,
  ElPagination,
  ElMessage,
  ElSkeleton,
  ElSkeletonItem,
  ElDialog,
  ElMessageBox,
  ElForm,
  ElFormItem,
  ElSelect,
  ElOption,
} from "element-plus";
import axios from "axios";
import config from "@/config.js";
import { UploadFilled, Plus } from "@element-plus/icons-vue";
import { getUploadSignature, uploadToOSS, beforeUpload } from "@/utils/uploadUtils";

export default {
  name: "MatrixPublishingTask",
  components: {
    ElInput,
    ElButton,
    ElPagination,
    ElSkeleton,
    ElSkeletonItem,
    ElDialog,
    ElForm,
    ElFormItem,
    ElSelect,
    ElOption,
    UploadFilled,
    Plus,
  },
  data() {
    return {
      searchQuery: "",
      currentPage: 1,
      pageSize: 15,
      total: 0,
      loadingData: true,
      tasks: [],
      // 模态框控制
      showAddVideoModal: false,
      showAddImageTextModal: false,
      videoAddLoading: false,
      imageTextAddLoading: false,
      // 视频上传进度
      videoProgress: 0,
      // 图片上传进度跟踪
      imageProgressMap: new Map(),
      // 新任务数据
      newVideoTask: {
        fb_title: "",
        fb_description: "",
        material_url: "",
        zh_ids: [],
      },
      newImageTextTask: {
        fb_title: "",
        fb_description: "",
        material_url: "",
        zh_ids: [],
      },
      // 授权账号选项
      authUserOptions: [],
      // 图片上传相关
      imageFileList: [],
      dialogVisible: false,
      dialogImageUrl: "",
      // 视频上传相关
      videoFileList: [],
    };
  },
  computed: {
    filteredTasks() {
      if (!this.searchQuery) return this.tasks;
      return this.tasks.filter(
        task =>
          task.fb_title.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
          task.authorizedAccount.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    },
    paginatedTasks() {
      // 接口返回已分页的数据
      return this.tasks;
    },
  },
  mounted() {
    const user = localStorage.getItem("user");
    if (!user) {
      this.$router.push({ name: "LoginPage" });
    } else {
      this.fetchReleaseTasks();
      this.fetchAuthUsers();
    }
  },
  methods: {
    fetchReleaseTasks() {
      const userId = this.$store.state.userId;
      const action = this.searchQuery ? "search" : "query";
      const params = {
        action,
        user_id: userId,
        page: this.currentPage,
        pageSize: this.pageSize,
      };
      if (this.searchQuery) {
        params.fb_title = this.searchQuery;
      }
      axios
        .get(`${config.BASE_URL}/pc/api/auth_release_api.php`, { params })
        .then((res) => {
          if (res.data.success) {
            this.tasks = res.data.data;
            this.total = res.data.total;
          } else {
            ElMessage.error(res.data.error || "接口返回错误");
          }
        })
        .catch((err) => {
          console.error("接口调用失败", err);
          ElMessage.error("接口调用失败");
        })
        .finally(() => {
          setTimeout(() => {
            this.loadingData = false;
          }, 500);
        });
    },
    fetchAuthUsers() {
      const userId = this.$store.state.userId;
      axios
        .get(`${config.BASE_URL}/pc/api/auth_user_api.php`, {
          params: { action: "query", user_id: userId },
        })
        .then((res) => {
          if (res.data.success) {
            this.authUserOptions = res.data.data;
          } else {
            ElMessage.error("获取授权账号失败");
          }
        })
        .catch((err) => {
          console.error("获取授权账号失败", err);
          ElMessage.error("获取授权账号失败");
        });
    },
    handleSearch() {
      this.currentPage = 1;
      this.loadingData = true;
      this.fetchReleaseTasks();
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.loadingData = true;
      this.fetchReleaseTasks();
    },
    getStatusClass(status) {
      switch (status) {
        case "已发送":
          return "status-sent";
        case "待发送":
          return "status-pending-send";
        case "待审核":
          return "status-pending-review";
        case "发送中":
          return "status-sending";
        case "发布中":
          return "status-publishing";
        default:
          return "status-unknown";
      }
    },
    // ----- 视频任务相关 -----
    openAddVideoModal() {
      this.showAddVideoModal = true;
    },
    closeAddVideoModal() {
      this.showAddVideoModal = false;
      this.newVideoTask = { fb_title: "", fb_description: "", material_url: "", zh_ids: [] };
      this.videoFileList = [];
    },
    // 自定义视频上传请求
    async uploadVideoRequest({ file, onSuccess, onError }) {
      try {

        this.videoProgress = 0 // 重置进度
        const config = {
          onUploadProgress: progressEvent => {
            const percent = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            )
            this.videoProgress = percent
          }
        }
        const valid = await beforeUpload(file, {
          maxSize: 200,
          allowedTypes: ["video/mp4", "video/avi", "video/mov", "video/flv", "video/webm"],
        });
        if (!valid) {
          ElMessage.error("视频格式或大小不符合要求");
          return;
        }
        const signature = await getUploadSignature("video");
        if (!signature) {
          ElMessage.error("获取上传签名失败");
          return;
        }
        const result = await uploadToOSS(file, signature, config);
        if (result.success) {
          this.videoProgress = 100
          setTimeout(() => this.videoProgress = 0, 2000) // 2秒后重置
          onSuccess(result)
          this.newVideoTask.material_url = result.url;
          this.videoFileList = [{ name: file.name, url: result.url }];
        } else {
          onError(new Error("视频上传失败"));
        }
      } catch (error) {
        console.error("视频上传请求失败:", error);
        onError(error);
      }
    },
    submitAddVideoTask() {
      if (!this.newVideoTask.fb_title || !this.newVideoTask.fb_description || !this.newVideoTask.material_url || this.newVideoTask.zh_ids.length === 0) {
        ElMessage.error("请填写所有必填字段并选择至少一个授权账号");
        return;
      }
      this.videoAddLoading = true;
      const userId = this.$store.state.userId;
      const payload = {
        user_id: userId,
        zh_ids: this.newVideoTask.zh_ids,
        material_url: this.newVideoTask.material_url,
        fb_title: this.newVideoTask.fb_title,
        fb_description: this.newVideoTask.fb_description,
        fb_topic: "",
        fb_poi: "",
        fb_shop: "",
        fb_miniprogram: "",
        fb_type: "video",
        fb_status: "待发送",
      };
      axios
        .post(`${config.BASE_URL}/pc/api/auth_release_api.php`, payload, {
          params: { action: "add" },
        })
        .then((res) => {
          if (res.data.success) {
            ElMessage.success("添加视频任务成功");
            this.closeAddVideoModal();
            this.fetchReleaseTasks();
          } else {
            ElMessage.error(res.data.error || "添加视频任务失败");
          }
        })
        .catch((err) => {
          console.error("添加视频任务失败", err);
          ElMessage.error("添加视频任务失败");
        })
        .finally(() => {
          this.videoAddLoading = false;
        });
    },
    // ----- 图文任务相关 -----
    openAddImageTextModal() {
      this.showAddImageTextModal = true;
    },
    closeAddImageTextModal() {
      this.showAddImageTextModal = false;
      this.newImageTextTask = { fb_title: "", fb_description: "", material_url: "", zh_ids: [] };
      this.imageFileList = [];
    },

    async uploadImageRequest({ file, onSuccess, onError }) {
      try {
        // 初始化进度
        this.imageProgressMap.set(file.uid, 0)

        const config = {
          onUploadProgress: progressEvent => {
            const percent = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            )
            this.imageProgressMap.set(file.uid, percent)
          }
        }

        // 获取上传签名
        const signature = await getUploadSignature("image");
        if (!signature) {
          ElMessage.error("获取上传签名失败");
          return false;
        }

        // 上传到 OSS
        const result = await uploadToOSS(file, signature, config);

        if (result.success) {
          // 🔴 关键修改点 1：用 OSS 地址替换本地预览地址
          file.url = result.url;
          file.status = 'success'; // 标记为上传成功

          // 🔴 关键修改点 2：更新文件列表时去重处理
          const existingIndex = this.imageFileList.findIndex(
            item => item.uid === file.uid
          );

          if (existingIndex === -1) {
            this.imageFileList.push({
              ...file,
              url: result.url,
              name: file.name
            });
          } else {
            this.imageFileList[existingIndex] = {
              ...file,
              url: result.url,
              name: file.name
            };
          }

          // 🔴 关键修改点 3：手动触发列表更新
          this.imageFileList = [...this.imageFileList];
          this.imageProgressMap.set(file.uid, 100)
          setTimeout(() => this.imageProgressMap.delete(file.uid), 2000)
          onSuccess(result)
          return false;
        } else {
          onError(new Error("图片上传失败"));
          return false;
        }
      } catch (error) {
        console.error("图片上传请求失败:", error);
        onError(error);
        return false;
      }
    },
    // 获取图片上传进度
    getImageProgress(file) {
      return this.imageProgressMap.get(file.uid) || 0
    },


    handlePictureCardPreview(file) {
      this.dialogImageUrl = file.url;
      this.dialogVisible = true;
    },
    submitAddImageTextTask() {
      if (
        !this.newImageTextTask.fb_title ||
        !this.newImageTextTask.fb_description ||
        this.imageFileList.length === 0 ||
        this.newImageTextTask.zh_ids.length === 0
      ) {
        ElMessage.error("请填写所有必填字段、上传至少一张图片并选择至少一个授权账号");
        return;
      }
      // 提取纯 OSS 链接（过滤本地 blob）
      const ossUrls = this.imageFileList
        .filter(file => file.url?.startsWith('http'))
        .map(file => file.url);

      if (ossUrls.length === 0) {
        ElMessage.error("请上传有效图片");
        return;
      }

      // 将 OSS 链接数组转为 JSON 字符串
      this.newImageTextTask.material_url = JSON.stringify(ossUrls);
      this.imageTextAddLoading = true;
      const userId = this.$store.state.userId;
      const payload = {
        user_id: userId,
        zh_ids: this.newImageTextTask.zh_ids,
        material_url: this.newImageTextTask.material_url,
        fb_title: this.newImageTextTask.fb_title,
        fb_description: this.newImageTextTask.fb_description,
        fb_topic: "",
        fb_poi: "",
        fb_shop: "",
        fb_miniprogram: "",
        fb_type: "tuwen", // 图文任务
        fb_status: "待发送",
      };
      axios
        .post(`${config.BASE_URL}/pc/api/auth_release_api.php`, payload, {
          params: { action: "add" },
        })
        .then((res) => {
          if (res.data.success) {
            ElMessage.success("添加图文任务成功");
            this.closeAddImageTextModal();
            this.fetchReleaseTasks();
          } else {
            ElMessage.error(res.data.error || "添加图文任务失败");
          }
        })
        .catch((err) => {
          console.error("添加图文任务失败", err);
          ElMessage.error("添加图文任务失败");
        })
        .finally(() => {
          this.imageTextAddLoading = false;
        });
    },

    // ----- 删除任务 -----
    handleDelete(task) {
      ElMessageBox.confirm(
        `确定删除任务 ${task.fb_title} 吗？`,
        "删除确认",
        {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning",
        }
      )
        .then(() => {
          const userId = this.$store.state.userId;
          axios
            .get(`${config.BASE_URL}/pc/api/auth_release_api.php`, {
              params: { action: "delete", id: task.id, user_id: userId },
            })
            .then((res) => {
              if (res.data.success) {
                ElMessage.success(`删除任务 ${task.fb_title} 成功`);
                this.fetchReleaseTasks();
              } else {
                ElMessage.error(res.data.error || "删除失败");
              }
            })
            .catch((err) => {
              console.error("删除请求失败", err);
              ElMessage.error("删除请求失败");
            });
        })
        .catch(() => {
          ElMessage.info("已取消删除");
        });
    },
  },
};
</script>

<style scoped>
.authorized-account-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.matrix-task-page {
  background-color: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

:deep(.el-upload-list__item) {
  transition: all 0.3s !important;
}

:deep(.el-upload-list__item .el-icon) {
  display: none !important;
}

:deep(.el-upload-list__item .el-upload-list__item-status-label) {
  display: none !important;
}

/* 顶部控制区 */
.top-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.search-input {
  width: 300px;
}

.pagination {
  margin-left: auto;
}

/* 按钮区 */
.button-area {
  margin-bottom: 20px;
  text-align: left;
  display: flex;
  gap: 10px;
}

.task-button {
  font-size: 16px;
  padding: 10px 20px;
  border-radius: 8px;
  transition: transform 0.3s ease, background-color 0.3s ease;
}

.task-button:hover {
  transform: scale(1.05);
}

.book-auth-button {
  background: linear-gradient(135deg, #ff7e5f, #feb47b);
  border: none;
  color: #fff;
  height: 40px;
}

.video-task-button {
  background: linear-gradient(135deg, #ff7e5f, #feb47b);
  border: none;
  color: #fff;
  height: 40px;
}

.image-text-task-button {
  background: linear-gradient(135deg, #6a11cb, #2575fc);
  border: none;
  color: #fff;
  height: 40px;
}

/* 骨架屏区域 */
.table-skeleton {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
}

.skeleton-row {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 5px;
}

/* 表格列表区域 */
.accounts-table {
  width: 100%;
  border-collapse: collapse;
}

.accounts-table th,
.accounts-table td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: center;
}

.accounts-table th {
  background-color: #f2f2f2;
}

.ellipsis {
  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.avatar-img {
  width: 40px;
  height: 40px;
  border-radius: 50%;
}

/* 操作栏按钮美化 */
.relogin-button {
  background: linear-gradient(135deg, #5cb85c, #4cae4c);
  border: none;
  color: #fff;
  padding: 5px 10px;
  border-radius: 4px;
  transition: transform 0.3s ease, background 0.3s ease;
  margin-right: 5px;
}

.relogin-button:hover {
  transform: scale(1.05);
  background: linear-gradient(135deg, #4cae4c, #5cb85c);
}

.delete-button {
  background: linear-gradient(135deg, #ff4d4f, #f5222d);
  border: none;
  border-radius: 4px;
  color: #fff;
  padding: 5px 10px;
  font-size: 12px;
  transition: transform 0.3s ease, background 0.3s ease;
}

.delete-button:hover {
  transform: scale(1.05);
  background: linear-gradient(135deg, #f5222d, #ff4d4f);
}

/* 状态样式 */
.status-sent {
  color: #006400;
  font-weight: bold;
}

.status-pending-send {
  color: #FF8C00;
  font-weight: bold;
}

.status-pending-review {
  color: #1E90FF;
  font-weight: bold;
}

.status-sending {
  color: #8A2BE2;
  font-weight: bold;
}

.status-publishing {
  color: #20B2AA;
  font-weight: bold;
}

.status-unknown {
  color: #FF0000;
  font-weight: bold;
}

/* 添加任务模态框 */
.el-dialog {
  text-align: left;
}

.add-form {
  margin: 20px 0;
}

.dialog-footer {
  text-align: right;
}

/* 上传组件调整 */
.upload-demo {
  width: 100%;
}

/* 视频上传样式 */
.video-upload {
  border: 2px dashed #409EFF;
  border-radius: 8px;
  padding: 20px;
  cursor: pointer;
  transition: border-color 0.3s;
  width: 90%;
}

.video-upload:hover {
  border-color: #337ecc;
}

.upload-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.upload-icon {
  font-size: 40px;
  color: #409EFF;
}

.progress-bar {
  width: 80%;
  margin-top: 10px;
}

/* 图片上传进度样式 */
.image-upload-item {
  position: relative;
  width: 100%;
  height: 100%;
}

.progress-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
}
</style>
